// Consultation form: product context, accessible validation, repeat-submit guard, non-PII events.
// The form posts through Shopify's native contact form; everything here is enhancement.
(() => {
  const form = document.getElementById('ConsultationForm');
  if (!form) return;

  const root = form.closest('[data-shop-url]');
  const params = new URLSearchParams(location.search);
  const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const rawHandle = (params.get('product') || '').trim().toLowerCase();
  const handle = HANDLE_RE.test(rawHandle) ? rawHandle : '';
  const source = (params.get('source') || '').replace(/[^a-z0-9_-]/gi, '').slice(0, 40);
  if (source) { const sourceField = form.querySelector('[data-consultation-source]'); if (sourceField) sourceField.value = source; }
  const store = {
    get: (k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
    del: (k) => { try { sessionStorage.removeItem(k); } catch (e) {} },
  };

  // Analytics: event names and page context only. Never names, contact details, free text or consent values.
  const track = (name, data) => {
    const payload = Object.assign({ page_type: 'consultation' }, data);
    const send = () => {
      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) { Shopify.analytics.publish(name, payload); return true; }
      } catch (e) {}
      return false;
    };
    // Load-time events can run before Shopify's analytics is ready; retry once the page has loaded.
    if (!send() && document.readyState !== 'complete') window.addEventListener('load', send, { once: true });
  };

  // The post-redirect #ConsultationForm jump resets focus after load, so apply focus again once loaded.
  const focusAfterLoad = (el) => {
    el.focus();
    if (document.readyState !== 'complete') window.addEventListener('load', () => setTimeout(() => el.focus(), 0), { once: true });
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-calendly-link]');
    if (link) track('calendly_click', { cta_location: link.dataset.calendlyLink }); // a click, not a booking
  });

  // Accepted submission (Shopify re-renders the page with the success state).
  if (form.querySelector('[data-consultation-success]')) {
    focusAfterLoad(form.querySelector('[data-consultation-success] h2')); // autofocus is not honoured after the post redirect
    const pending = store.get('consultation-pending');
    if (pending) track('consultation_submit_accepted', { product_handle: pending.product_handle || undefined });
    store.del('consultation-pending');
    store.del('consultation-draft');
    return;
  }

  // Arrival from a consultation CTA (?source=pdp|custom-design|contact). Once per session per source/product.
  if (source) {
    const key = `consultation-cta:${source}:${handle}`;
    if (!store.get(key)) {
      store.set(key, 1);
      track('consultation_cta_click', { cta_location: source, product_handle: handle || undefined });
    }
  }

  const $ = (id) => document.getElementById(`ConsultationForm-${id}`);
  const summary = $('summary');
  const summaryList = summary.querySelector('[data-summary-list]');
  const button = form.querySelector('[data-consultation-submit]');
  const status = form.querySelector('[data-consultation-status]');
  const retry = form.querySelector('[data-consultation-retry]');
  const buttonLabel = button.textContent;
  const draftFields = ['name', 'email', 'interest', 'body', 'phone', 'occasion'];

  // Server-side errors: restore what the customer typed and report the failure.
  if (summary.dataset.serverErrors !== undefined) {
    const draft = store.get('consultation-draft') || {};
    draftFields.forEach((id) => { if (!$(id).value && draft[id]) $(id).value = draft[id]; });
    if (draft.whatsapp) $('whatsapp').checked = true;
    track('consultation_validation_error', { fields: summary.dataset.serverErrors, source: 'server' });
    focusAfterLoad(summary);
  }

  // ---- Product context (enhancement only) ----
  const chip = form.querySelector('[data-product-chip]');
  const chipLink = chip.querySelector('[data-product-chip-link]');
  const chipRemove = chip.querySelector('[data-product-chip-remove]');
  const productFields = {};
  form.querySelectorAll('[data-product-field]').forEach((i) => { productFields[i.dataset.productField] = i; });
  const setProduct = (values) => {
    Object.keys(productFields).forEach((k) => {
      const input = productFields[k];
      input.value = (values && values[k]) || '';
      input.disabled = !input.value;
    });
    chip.hidden = !values;
  };

  if (handle) {
    fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p || !p.id || p.handle !== handle) return; // deleted/redirected handles never attach a different product
        const variantParam = params.get('variant');
        // Only real choices carry a variant; single "Default Title" products do not.
        const variant = p.variants && p.variants.length > 1 && p.variants.find((v) => String(v.id) === variantParam);
        const url = new URL(`/products/${p.handle}`, root.dataset.shopUrl || location.origin).href;
        setProduct({ title: variant ? `${p.title} - ${variant.title}` : p.title, url, handle: p.handle, id: String(p.id), variant: variant ? String(variant.id) : '' });
        chipLink.textContent = productFields.title.value;
        chipLink.href = url;
        chipRemove.setAttribute('aria-label', `Remove ${productFields.title.value} from your enquiry`);
        const interest = $('interest');
        if (!interest.value && [...interest.options].some((o) => o.value === 'A specific piece')) interest.value = 'A specific piece';
      })
      .catch(() => {}); // invalid, deleted or unreachable: carry on as a general enquiry
  }

  chipRemove.addEventListener('click', () => {
    setProduct(null);
    $('interest').focus();
  });

  // ---- Validation ----
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const checks = {
    name: (v) => (v.trim() ? '' : 'Enter your name.'),
    email: (v) => (!v.trim() ? 'Enter your email address.' : EMAIL_RE.test(v.trim()) ? '' : 'Enter an email address in the format name@example.com.'),
    interest: (v) => (v ? '' : 'Choose what you are interested in.'),
    body: (v) => (!v.trim() ? "Tell us about what you're looking for." : v.trim().length < 10 ? 'Tell us a little more (at least 10 characters).' : ''),
  };
  const showError = (id, message) => {
    const field = $(id);
    const error = $(`${id}-error`);
    error.textContent = message;
    error.hidden = !message;
    if (message) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
  };

  form.noValidate = true; // JS takes over; without JS the browser's native required checks apply.
  let started = false;
  let sending = false;
  let timer;

  const recheck = (e) => {
    const id = (e.target.id || '').replace('ConsultationForm-', '');
    if (checks[id] && e.target.getAttribute('aria-invalid')) showError(id, checks[id](e.target.value));
  };
  form.addEventListener('input', (e) => {
    if (!started) { started = true; track('consultation_form_start', { product_handle: handle || undefined }); }
    recheck(e);
  });
  form.addEventListener('change', recheck);

  const resetButton = () => {
    sending = false;
    clearTimeout(timer);
    button.removeAttribute('aria-disabled');
    button.textContent = buttonLabel;
  };

  // Capture phase on document: runs before Shopify's hCaptcha listener on the form, which would otherwise
  // submit programmatically even after preventDefault. Blocked submits must stop propagation too.
  document.addEventListener('submit', (e) => {
    if (e.target !== form) return;
    if (sending) { e.preventDefault(); e.stopImmediatePropagation(); return; } // repeat click / double submit

    const failed = Object.keys(checks).filter((id) => {
      const message = checks[id]($(id).value);
      showError(id, message);
      return message;
    });

    if (failed.length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      summaryList.innerHTML = '';
      failed.forEach((id) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#ConsultationForm-${id}`;
        a.className = 'link';
        a.textContent = $(`${id}-error`).textContent;
        a.addEventListener('click', (ev) => { ev.preventDefault(); $(id).focus(); });
        li.appendChild(a);
        summaryList.appendChild(li);
      });
      summary.hidden = false;
      summary.setAttribute('role', 'alert');
      summary.focus();
      track('consultation_validation_error', { fields: failed.join(','), source: 'client' });
      return;
    }

    const draft = {};
    draftFields.forEach((id) => { draft[id] = $(id).value; });
    draft.whatsapp = $('whatsapp').checked;
    store.set('consultation-draft', draft);
    store.set('consultation-pending', { product_handle: productFields.handle.disabled ? '' : productFields.handle.value });

    sending = true;
    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Sending…';
    status.textContent = 'Sending your enquiry…';
    retry.hidden = true;
    timer = setTimeout(() => {
      // Still on this page after 20s: never claim success; let the customer retry or use another channel.
      resetButton();
      status.textContent = 'This is taking longer than expected. Your enquiry may not have been sent.';
      retry.hidden = false;
      track('consultation_submit_error', { reason: 'timeout' });
    }, 20000);
  }, true);

  // Back/forward cache restores the page mid-"Sending": unlock it.
  window.addEventListener('pageshow', (e) => { if (e.persisted) { resetButton(); status.textContent = ''; } });
})();
